import { useLocale } from 'locale';
import React from 'react';
import * as Styled from './styled';
import { PoiId } from 'utils/entities';
import PhoneIcon from 'assets/svg/phone-new.svg';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { PrivateContactProps } from 'components/contact/types';
import UserPlaceholder from 'assets/svg/profile-new.svg';
import { ContactAgentModalSource, ContactModalOpenMeta } from 'store/state/app';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { Button } from 'ds/components/button';
import CheckIcon from 'assets/svg/ok-check.svg';
import MailIcon from 'assets/svg/send.svg';
import ScrollIntoView from 'scroll-into-view';
import { useABTests } from 'hooks/useABTests';
import { ListingPageV5 } from 'config/abTests';
import ContactButtons from 'components/contact-buttons';
import useWhatsAppHref from 'hooks/useWhatsAppHref';
import _ from 'lodash';
import ContactsWithWhatsAppExpandable from 'components/contact/whatsapp-expandable';

interface POIPrivateStickyProps extends PrivateContactProps {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

export const POIPrivateSticky: React.FC<POIPrivateStickyProps> = (props) => {
  const {
    onCallClickEvent,
    contactValues,
    onSetContactModalPoiId,
    isGalleryOpen,
    isContacted,
    name,
    phone,
    poiType,
    id,
    poi,
    user,
    leadApi3Type,
    whatsappNumber,
  } = props;

  const { t } = useLocale();
  const { listingPageV5 } = useABTests();
  const screenBreakpoint = useScreenBreakpoint();
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = React.useState(false);
  const validPhoneNumber = Boolean(whatsappNumber);
  const whatsAppHref = useWhatsAppHref(whatsappNumber, name, id);

  const isModeB = listingPageV5 === ListingPageV5.modeB;
  const isMobile = screenBreakpoint < 3;

  const handleModalOpen = () => {
    onSetContactModalPoiId(id, { source: ContactAgentModalSource.ContactSticky });
  };

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || phoneNumberIsVisible, { ...contactValues, id, type: poiType }, ContactAgentModalSource.ContactSticky);

    if (!phoneNumberIsVisible && !isMobile) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  const scrollToForm = () => {
    const targetScrollEl = document.querySelector<HTMLElement>('[data-contact-form-type="side"], [data-contact-form-type="inner"]');
    if (targetScrollEl) {
      const scrollSettings = {
        time: 300,
        align: {
          topOffset: 125,
          top: 0,
        },
      };
      ScrollIntoView(targetScrollEl, scrollSettings);
    }
  };

  const handleClick = () => {
    if (!isGalleryOpen && isModeB) scrollToForm();
    else handleModalOpen();
  };

  if (isMobile) {
    return (
      <Styled.Root data-auto="private-poi-sticky">
        <Styled.ButtonWrapper>
          <ContactsWithWhatsAppExpandable
            poi={poi}
            leadApi3Type={leadApi3Type}
            user={user}
            whatsAppHref={whatsAppHref}
            whatsappNumber={whatsappNumber}
            userPhone={contactValues.phone}
            validPhoneNumber={validPhoneNumber}
          >
            <Styled.PhoneButton
              data-auto="phone-number-button"
              size="extraLarge"
              weight="medium"
              onClick={handlePhoneSubmit}
              href={createPhoneURI(phone)}
            >
              <PhoneIcon />
              {phoneNumberIsVisible ? phone : t('contactAgentModal.callButton')}
            </Styled.PhoneButton>
          </ContactsWithWhatsAppExpandable>
          {validPhoneNumber ? null : (
            <>
              {isContacted ? (
                <Button isInitialIconColor disabled data-auto="phone-private-inner-number-button-modal-disabled" size="extraLarge" mode="ghost">
                  <CheckIcon width={24} height={24} />
                  {t('contactAgentModal.button.disabled.modeB.static')}
                </Button>
              ) : (
                <Button isInitialIconColor onClick={handleClick} data-auto="phone-private-inner-number-button-modal" size="extraLarge" mode="ghost">
                  <MailIcon width={24} height={24} />
                  {t(isModeB ? 'contactAgentModal.mobileButton.modeB' : 'contactAgentModal.open')}
                </Button>
              )}
            </>
          )}
        </Styled.ButtonWrapper>
      </Styled.Root>
    );
  }

  return (
    <Styled.Root data-auto="private-poi-sticky">
      <Styled.InfoBox>
        <UserPlaceholder width={56} height={56} />
        <Styled.UserName weight="bold">{name}</Styled.UserName>
      </Styled.InfoBox>

      <ContactButtons
        phone={phone}
        onShowPhone={handlePhoneSubmit}
        isContacted={isContacted}
        handleModalOpen={handleModalOpen}
        showPhone={phoneNumberIsVisible}
        dataAttributePrefix="private-contact-poi"
      />
    </Styled.Root>
  );
};
